// Badge Journey card — loyalty-style card on the trainee home showing the learner's
// next badge milestone (image, name, progress) with an unlock action when ready.
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, mediaUrl } from '@/lib/api';
import { Award, Lock, Sparkles, ChevronRight } from 'lucide-react';
import BadgeQuizModal from '@/components/BadgeQuizModal';

const colorClass = (c) => ({
    gold: 'from-amber-500 to-orange-500',
    emerald: 'from-emerald-500 to-teal-600',
    blue: 'from-blue-500 to-indigo-600',
    violet: 'from-violet-500 to-fuchsia-500',
    rose: 'from-rose-500 to-pink-500',
    slate: 'from-slate-600 to-slate-800',
}[c] || 'from-amber-500 to-orange-500');

export default function BadgeJourneyCard() {
    const [data, setData] = useState(null);
    const [quizBadge, setQuizBadge] = useState(null);

    const load = useCallback(() => {
        api.get('/trainee/badge-progress')
            .then((r) => setData(r.data))
            .catch(() => setData({ points_total: 0, badges: [] }));
    }, []);

    useEffect(() => { load(); }, [load]);

    if (!data || !data.badges?.length) return null;

    // Next target: a ready-to-unlock badge first, else the closest locked one.
    const unlockable = data.badges.find((b) => b.unlockable);
    const locked = data.badges
        .filter((b) => !b.earned && !b.unlockable && b.source !== 'offline')
        .sort((a, b) => a.points_away - b.points_away)[0];
    const offlineNext = data.badges.find((b) => !b.earned && b.source === 'offline');
    const earnedBadges = data.badges.filter((b) => b.earned);
    const earnedCount = earnedBadges.length;
    const target = unlockable || locked || offlineNext;

    // All badges earned → show a celebratory "journey complete" card instead of hiding.
    if (!target) {
        // Showcase the most recently earned badge (by award date), else the highest tier.
        const showcase = [...earnedBadges]
            .sort((a, b) => (b.awarded_at || '').localeCompare(a.awarded_at || ''))[0]
            || earnedBadges[earnedBadges.length - 1];
        if (!showcase) return null;
        return (
            <div className="max-w-xl mx-auto w-full" data-testid="badge-journey-card">
                <Link to="/learn/badges"
                    className="relative block rounded-2xl overflow-hidden p-[1.5px] bg-gradient-to-br from-brand-yellow/70 via-brand-yellow/20 to-transparent group"
                    data-testid="badge-journey-link">
                    <div className="rounded-[14px] bg-brand-navy px-5 py-4">
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-brand-yellow/80 text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5">
                                <Sparkles className="w-3.5 h-3.5" /> Badge Journey
                            </span>
                            <span className="text-white/50 text-[11px] font-semibold">{earnedCount}/{data.badges.length} earned</span>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="relative shrink-0">
                                {showcase.image_url
                                    ? <img src={mediaUrl(showcase.image_url)} alt={showcase.name} className="h-16 w-16 rounded-2xl object-cover" />
                                    : <div className={`h-16 w-16 rounded-2xl grid place-items-center font-display font-black text-white text-2xl bg-gradient-to-br ${colorClass(showcase.color)}`}>{showcase.name?.[0]}</div>}
                                <div className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-brand-navy grid place-items-center ring-2 ring-brand-navy">
                                    <Award className="w-3.5 h-3.5 text-brand-yellow" />
                                </div>
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-brand-yellow/70 text-[10px] font-bold uppercase tracking-wider">Latest badge</div>
                                <div className="font-bold text-white text-base leading-tight truncate" data-testid="badge-journey-name">{showcase.name}</div>
                                <div className="text-brand-yellow text-xs font-bold mt-0.5">All {data.badges.length} badge{data.badges.length !== 1 ? 's' : ''} earned 🏆</div>
                            </div>
                            <ChevronRight className="w-5 h-5 text-white/40 shrink-0 group-hover:translate-x-0.5 transition-transform" />
                        </div>
                    </div>
                </Link>
            </div>
        );
    }

    return (
        <div className="max-w-xl mx-auto w-full" data-testid="badge-journey-card">
            <Link
                to="/learn/badges"
                onClick={(e) => { if (target.unlockable) { e.preventDefault(); setQuizBadge(target); } }}
                className="relative block rounded-2xl overflow-hidden p-[1.5px] bg-gradient-to-br from-brand-yellow/70 via-brand-yellow/20 to-transparent group"
                data-testid="badge-journey-link">
                <div className="rounded-[14px] bg-brand-navy px-5 py-4">
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-brand-yellow/80 text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5">
                            <Sparkles className="w-3.5 h-3.5" /> Badge Journey
                        </span>
                        <span className="text-white/50 text-[11px] font-semibold">{earnedCount}/{data.badges.length} earned</span>
                    </div>

                    <div className="flex items-center gap-4">
                        {/* Badge medallion */}
                        <div className="relative shrink-0">
                            {target.image_url
                                ? <img src={mediaUrl(target.image_url)} alt={target.name}
                                    className={`h-16 w-16 rounded-2xl object-cover ${target.unlockable ? '' : 'grayscale opacity-60'}`} />
                                : <div className={`h-16 w-16 rounded-2xl grid place-items-center font-display font-black text-white text-2xl bg-gradient-to-br ${colorClass(target.color)} ${target.unlockable ? '' : 'opacity-70'}`}>{target.name?.[0]}</div>}
                            <div className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-brand-navy grid place-items-center ring-2 ring-brand-navy">
                                {target.unlockable
                                    ? <Award className="w-3.5 h-3.5 text-brand-yellow" />
                                    : <Lock className="w-3.5 h-3.5 text-white/50" />}
                            </div>
                        </div>

                        <div className="flex-1 min-w-0">
                            <div className="font-bold text-white text-base leading-tight truncate" data-testid="badge-journey-name">{target.name}</div>
                            {target.source === 'offline' ? (
                                <div className="text-white/60 text-xs font-semibold mt-0.5" data-testid="badge-journey-offline">
                                    Earn at an offline training
                                </div>
                            ) : target.unlockable ? (
                                <div className="text-brand-yellow text-xs font-bold mt-0.5">Ready! Take the quiz to unlock</div>
                            ) : (
                                <div className="text-white/60 text-xs font-semibold mt-0.5" data-testid="badge-journey-away">
                                    {target.points_away} pts away · {target.points_required} pts
                                </div>
                            )}
                            {target.source !== 'offline' && (
                                <div className="mt-2 h-1.5 w-full bg-white/15 rounded-full overflow-hidden">
                                    <div className="h-full bg-brand-yellow rounded-full transition-all" style={{ width: `${target.progress_pct}%` }} />
                                </div>
                            )}
                        </div>

                        {target.unlockable ? (
                            <span className="shrink-0 px-3 py-1.5 rounded-xl bg-brand-yellow text-brand-black text-xs font-bold" data-testid="badge-journey-unlock-btn">
                                Unlock
                            </span>
                        ) : (
                            <ChevronRight className="w-5 h-5 text-white/40 shrink-0 group-hover:translate-x-0.5 transition-transform" />
                        )}
                    </div>
                </div>
            </Link>

            {quizBadge && (
                <BadgeQuizModal
                    badgeId={quizBadge.id}
                    onClose={() => setQuizBadge(null)}
                    onEarned={load}
                />
            )}
        </div>
    );
}
